/**
 * Utility to lookup OPN/GPN image info using the imagemetadata API service
 */

export default class DAMImageUtil {
    private apiUrl: string = '/imagemetadata/api';

    // in-memory cache to avoid unnecessary fetch calls
    private static cache: Map<string, DAMImage[]> = new Map<string, DAMImage[]>();

    private static instance: DAMImageUtil;

    public constructor(apiUrl?: string) {
        this.apiUrl = apiUrl || this.apiUrl;
    }

    public static getInstance(apiUrl?: string) {
        if (DAMImageUtil.instance && (!apiUrl || DAMImageUtil.instance.apiUrl == apiUrl)) {
            return DAMImageUtil.instance;
        }
        DAMImageUtil.instance = new DAMImageUtil(apiUrl);
        return DAMImageUtil.instance;
    }

    /**
     * Lookup the images for a part from the imagemetadata API
     *
     * @param partNumber - generic or orderable part number
     * @param locale - locale
     * @param partType - the type of the partNumber parameter - 'gpn' or 'opn'
     * @param imageType - image type to lookup for the part
     * @returns Promise of DamImage[]
     */
    public async getImages(partNumber: string, locale: string, partType: string, imageType: string): Promise<DAMImage[]> {
        const key = `${this.apiUrl}|${partNumber.toUpperCase()}|${locale}|${partType.toLowerCase()}|${imageType}`;
        if (!DAMImageUtil.cache.has(key)) {
            const images: DAMImage[] = await this.fetchImageData(partNumber, locale, partType, imageType);
            DAMImageUtil.cache.set(key, images);
        }
        return DAMImageUtil.cache.get(key);
    }

    /**
     * Lookup the first image with sortOrder=1 for a part from the imagemetadata API
     *
     * @param partNumber - generic or orderable part number
     * @param locale - locale
     * @param partType - the type of the partNumber parameter - 'gpn' or 'opn'
     * @param imageType - image type to lookup for the part
     * @param rendition - optional - rendition of the image to use
     * @returns Promise of string - the image url
     */
    public async getPrimaryImageUrl(partNumber: string, locale: string, partType: string, imageType: string, rendition?: string): Promise<string> {
        const images: DAMImage[] = await this.getImages(partNumber, locale, partType, imageType);
        const image: DAMImage = images.find(image => image.sortOrder === 1);
        return this.getImageUrl(image, rendition);
    }

    /**
     * Lookup the first image for a part from the imagemetadata API
     *
     * @param partNumber - generic or orderable part number
     * @param locale - locale
     * @param partType - the type of the partNumber parameter - 'gpn' or 'opn'
     * @param imageType - image type to lookup for the part
     * @param rendition - optional - rendition of the image to use
     * @returns Promise of string - the image url
     */
    public async getFirstImageUrl(partNumber: string, locale: string, partType: string, imageType: string, rendition?: string): Promise<string> {
        const images: DAMImage[] = await this.getImages(partNumber, locale, partType, imageType);
        const image: DAMImage = (images && images.length && images[0].imageUrl) ? images[0] : null;
        return this.getImageUrl(image, rendition);
    }

    private getImageUrl(image: DAMImage, rendition?: string) {
        let imageUrl: string = '';
        if (image && image.imageUrl) {
            imageUrl = image.imageUrl;
            if (rendition && image.isPim) {
                imageUrl = imageUrl.concat(`:${rendition}`);
            }
        }
        return imageUrl;
    }

    private async fetchImageData(partNumber: string, locale: string, partType: string, imageType: string): Promise<DAMImage[]> {
        try {
            const url = `${this.apiUrl}/${locale}/${partType.toLowerCase()}/${partNumber.toUpperCase().replace(/\//g, '@')}${imageType ? '?imageType=' + imageType : ''}`;
            const response = await fetch(url);
            if (!response.ok) {
                return [];
            }
            const json = await response.json();
            const images: DAMImage[] = json as DAMImage[];
            return images || [];
        }
        catch (err) {
            return [];
        }
    }
}

/**
 * Response structure expected from imagemetadata API service
 */
interface DAMImage {
    gpn: string,
    opn: string,
    imageType: string,
    imageUrl: string,
    imageTitle: string,
    imageAltTag: string,
    sortOrder: number,
    isPim: boolean
}
